import { FilterRule, FilterOperator } from '@/types/adjustment-filters';

/**
 * Evaluates a single filter rule against a line item
 */
export const evaluateFilterRule = (
  rule: FilterRule,
  lineItem: {
    category_id?: string;
    trade_id?: string;
    quantity_in_base_units: number;
    base_cost: number;
  }
): boolean => {
  const { field, operator, value } = rule;

  let fieldValue: any;

  switch (field) {
    case 'category':
      fieldValue = lineItem.category_id;
      break;
    case 'trade':
      fieldValue = lineItem.trade_id;
      break;
    case 'quantity':
      fieldValue = lineItem.quantity_in_base_units;
      break;
    case 'base_cost':
      fieldValue = lineItem.base_cost;
      break;
    default:
      return false;
  }

  return evaluateOperator(fieldValue, operator, value);
};

/**
 * Evaluates an operator against two values
 */
const evaluateOperator = (
  fieldValue: any,
  operator: FilterOperator,
  ruleValue: string | number | [number, number]
): boolean => {
  switch (operator) {
    case '=':
      return fieldValue === ruleValue;
    case '>':
      return Number(fieldValue) > Number(ruleValue);
    case '<':
      return Number(fieldValue) < Number(ruleValue);
    case '>=':
      return Number(fieldValue) >= Number(ruleValue);
    case '<=':
      return Number(fieldValue) <= Number(ruleValue);
    case 'between':
      if (Array.isArray(ruleValue) && ruleValue.length === 2) {
        const numValue = Number(fieldValue);
        return numValue >= ruleValue[0] && numValue <= ruleValue[1];
      }
      return false;
    default:
      return false;
  }
};

/**
 * Evaluates all filter rules against a line item using AND/OR logic
 */
export const evaluateFilterRules = (
  rules: FilterRule[],
  lineItem: {
    category_id?: string;
    trade_id?: string;
    quantity_in_base_units: number;
    base_cost: number;
  }
): boolean => {
  if (rules.length === 0) return false;

  let result = evaluateFilterRule(rules[0], lineItem);

  for (let i = 1; i < rules.length; i++) {
    const previousRule = rules[i - 1];
    const currentRuleResult = evaluateFilterRule(rules[i], lineItem);

    if (previousRule.logic === 'OR') {
      result = result || currentRuleResult;
    } else {
      // Default to AND
      result = result && currentRuleResult;
    }
  }

  return result;
};

/**
 * Gets the source line item data for an estimate line item
 */
export const getSourceLineItemData = async (
  componentType: string,
  componentId: string
): Promise<{
  category_id?: string;
  trade_id?: string;
  base_cost: number;
} | null> => {
  const { supabase } = await import('@/integrations/supabase/client');

  if (componentType === 'internal_line_item') {
    const { data } = await supabase
      .from('internal_line_items')
      .select('category_id, base_cost')
      .eq('id', componentId)
      .maybeSingle();

    if (!data) return null;

    // Get trade_id from mapping table
    const { data: tradeMapping } = await supabase
      .from('internal_line_item_trade_mappings')
      .select('trade_id')
      .eq('line_item_id', componentId)
      .maybeSingle();

    return {
      category_id: data.category_id || undefined,
      trade_id: tradeMapping?.trade_id || undefined,
      base_cost: data.base_cost
    };
  } else if (componentType === 'supplier_line_item') {
    const { data } = await supabase
      .from('internal_line_items')
      .select('category_id, base_cost')
      .eq('id', componentId)
      .maybeSingle();

    if (!data) return null;

    return {
      category_id: data.category_id || undefined,
      trade_id: undefined,
      base_cost: data.base_cost
    };
  }

  return null;
};
